import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutGrid, MessageSquare, Quote, User, LogOut, Plus, Pencil, Trash2, Mail, ExternalLink, Download, CalendarDays, Clock, RefreshCw } from "lucide-react";
import { api, fmtPrice, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PropertyDialog, TestimonialDialog, OpenHouseDialog } from "../components/admin/AdminForms";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";

const NAV = [
  { id: "inquiries", label: "Inquiries", icon: MessageSquare },
  { id: "leads", label: "Guide Leads", icon: Download },
  { id: "listings", label: "Listings", icon: LayoutGrid },
  { id: "openhouses", label: "Open Houses", icon: CalendarDays },
  { id: "testimonials", label: "Testimonials", icon: Quote },
  { id: "profile", label: "Profile", icon: User },
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [properties, setProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [leads, setLeads] = useState([]);
  const [profile, setProfile] = useState(null);
  const [openHouses, setOpenHouses] = useState([]);
  const [propDialog, setPropDialog] = useState({ open: false, initial: null });
  const [testDialog, setTestDialog] = useState({ open: false, initial: null });
  const [ohDialog, setOhDialog] = useState({ open: false, initial: null });
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);

  const load = useCallback(() => {
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/profile").then((r) => setProfile(r.data));
    api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {});
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/open-houses").then((r) => setOpenHouses(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading || !user) return <div className="min-h-screen bg-[#F8F9FA]" />;

  const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

  const saveProperty = async (data) => {
    try {
      if (propDialog.initial?.id) await api.put(`/properties/${propDialog.initial.id}`, data);
      else await api.post("/properties", data);
      toast.success("Listing saved"); setPropDialog({ open: false, initial: null }); load();
    } catch (e) { err(e); }
  };
  const delProperty = async (id) => { if (!window.confirm("Delete this listing?")) return; try { await api.delete(`/properties/${id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };

  const saveTestimonial = async (data) => {
    try {
      if (testDialog.initial?.id) await api.put(`/testimonials/${testDialog.initial.id}`, data);
      else await api.post("/testimonials", data);
      toast.success("Testimonial saved"); setTestDialog({ open: false, initial: null }); load();
    } catch (e) { err(e); }
  };
  const delTestimonial = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/testimonials/${id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };

  const saveOpenHouse = async (data) => {
    try {
      if (ohDialog.initial?.id) await api.put(`/open-houses/${ohDialog.initial.id}`, data);
      else await api.post("/open-houses", data);
      toast.success("Open house saved"); setOhDialog({ open: false, initial: null }); load();
    } catch (e) { err(e); }
  };
  const delOpenHouse = async (id) => { if (!window.confirm("Delete this open house?")) return; try { await api.delete(`/open-houses/${id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };

  const syncRemax = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/sync-remax");
      toast.success(`RE/MAX feed synced: ${r.data.synced} listings (${r.data.added} new, ${r.data.updated} updated)`);
      load();
    } catch (e) { err(e); } finally { setSyncing(false); }
  };

  const markRead = async (id) => { try { await api.patch(`/inquiries/${id}`); load(); } catch (e) { err(e); } };
  const delInquiry = async (id) => { if (!window.confirm("Delete inquiry?")) return; try { await api.delete(`/inquiries/${id}`); toast.success("Deleted"); load(); } catch (e) { err(e); } };

  const unread = inquiries.filter((i) => !i.read).length;

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      <aside className="w-60 bg-[#0A1128] text-[#FAF9F6] flex flex-col fixed h-screen">
        <div className="p-6 border-b border-white/10">
          <p className="font-serif text-2xl">{profile?.name || "Agent"}</p>
          <p className="text-xs text-white/50 uppercase tracking-widest mt-1">Dashboard</p>
        </div>
        <nav className="flex-1 py-6">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`admin-tab-${n.id}`} onClick={() => setTab(n.id)}
              className={`w-full flex items-center gap-3 px-6 py-3.5 text-sm transition-colors ${tab === n.id ? "bg-white/10 text-white border-l-2 border-[var(--gold)]" : "text-white/60 hover:text-white"}`}>
              <n.icon size={17} /> {n.label}
              {n.id === "inquiries" && unread > 0 && <span className="ml-auto bg-[var(--gold)] text-[#0A1128] text-xs px-2 py-0.5 rounded-full">{unread}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-2 py-2 text-sm text-white/60 hover:text-white transition-colors"><ExternalLink size={16} /> View Site</a>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center gap-3 px-2 py-2 text-sm text-white/60 hover:text-white transition-colors"><LogOut size={16} /> Sign Out</button>
        </div>
      </aside>

      <main className="flex-1 ml-60 p-10">
        {tab === "inquiries" && (
          <Section title="Inquiries" subtitle={`${inquiries.length} total · ${unread} unread`}>
            {inquiries.length === 0 ? <Empty text="No inquiries yet." /> : (
              <div className="space-y-4">
                {inquiries.map((i) => (
                  <div key={i.id} data-testid={`inquiry-${i.id}`} className={`bg-white border p-6 ${i.read ? "border-[#0A1128]/10" : "border-[var(--gold)]"}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-serif text-xl text-[#0A1128]">{i.name}</h3>
                          {!i.read && <Badge className="bg-[var(--gold)] text-[#0A1128] hover:bg-[var(--gold)]">New</Badge>}
                        </div>
                        <p className="text-sm text-[var(--slate)] mt-1 flex items-center gap-4">
                          <a href={`mailto:${i.email}`} className="flex items-center gap-1 hover:text-[#0A1128]"><Mail size={13} /> {i.email}</a>
                          {i.phone && <span>{i.phone}</span>}
                        </p>
                        {i.property_title && <p className="text-xs uppercase tracking-wider text-[var(--gold)] mt-2">Re: {i.property_title}</p>}
                        <p className="text-[var(--slate)] mt-3 leading-relaxed">{i.message}</p>
                        <p className="text-xs text-[var(--slate)]/60 mt-3">{new Date(i.created_at).toLocaleString()}</p>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        {!i.read && <button data-testid={`inquiry-read-${i.id}`} onClick={() => markRead(i.id)} className="text-xs px-3 py-2 border border-[#0A1128]/20 hover:bg-[#E8E4D9]/40 transition-colors">Mark Read</button>}
                        <button data-testid={`inquiry-delete-${i.id}`} onClick={() => delInquiry(i.id)} className="text-xs px-3 py-2 text-red-600 hover:bg-red-50 transition-colors flex items-center gap-1"><Trash2 size={13} /> Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === "leads" && (
          <Section title="Guide Leads" subtitle={`${leads.length} buyer-guide downloads`}>
            {leads.length === 0 ? <Empty text="No guide downloads yet." /> : (
              <div className="bg-white border border-[#0A1128]/10">
                {leads.map((l) => (
                  <div key={l.id} data-testid={`lead-${l.id}`} className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[#0A1128]/10 last:border-0">
                    <div>
                      <p className="font-medium text-[#0A1128]">{l.name || "—"}</p>
                      <a href={`mailto:${l.email}`} className="text-sm text-[var(--slate)] hover:text-[#0A1128] flex items-center gap-1"><Mail size={13} /> {l.email}</a>
                    </div>
                    <div className="flex items-center gap-4">
                      {l.pack && <Badge className="bg-[#0A1128] hover:bg-[#0A1128] capitalize">{l.pack}</Badge>}
                      <span className="text-xs text-[var(--slate)]/60">{new Date(l.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === "listings" && (
          <Section title="Listings" subtitle={`${properties.length} properties`}
            action={
              <div className="flex gap-3">
                <button data-testid="sync-remax" onClick={syncRemax} disabled={syncing}
                  className="flex items-center gap-2 px-5 py-3 border border-[#0A1128] text-[#0A1128] text-sm uppercase tracking-wider hover:bg-[#E8E4D9]/40 transition-colors disabled:opacity-50">
                  <RefreshCw size={16} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync RE/MAX Feed"}
                </button>
                <AddBtn testid="add-listing" onClick={() => setPropDialog({ open: true, initial: null })}>Add Listing</AddBtn>
              </div>
            }>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {properties.map((p) => (
                <div key={p.id} data-testid={`admin-listing-${p.id}`} className="bg-white border border-[#0A1128]/10">
                  <img src={p.images?.[0]} alt={p.title} className="w-full h-40 object-cover" />
                  <div className="p-5">
                    <div className="flex justify-between items-start">
                      <h3 className="font-serif text-lg text-[#0A1128]">{p.title}</h3>
                      {p.featured && <Badge className="bg-[#0A1128] hover:bg-[#0A1128]">Featured</Badge>}
                    </div>
                    <p className="text-sm text-[var(--slate)]">{p.location}</p>
                    <p className="font-serif text-lg text-[var(--gold)] mt-1">{fmtPrice(p.price, p.currency)}</p>
                    <div className="flex gap-2 mt-4">
                      <button data-testid={`edit-listing-${p.id}`} onClick={() => setPropDialog({ open: true, initial: p })} className="flex-1 flex items-center justify-center gap-1 text-sm py-2 border border-[#0A1128]/20 hover:bg-[#E8E4D9]/40 transition-colors"><Pencil size={13} /> Edit</button>
                      <button data-testid={`delete-listing-${p.id}`} onClick={() => delProperty(p.id)} className="px-3 py-2 text-red-600 border border-red-200 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === "testimonials" && (
          <Section title="Testimonials" subtitle={`${testimonials.length} reviews`}
            action={<AddBtn testid="add-testimonial" onClick={() => setTestDialog({ open: true, initial: null })}>Add Testimonial</AddBtn>}>
            <div className="grid md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white border border-[#0A1128]/10 p-6">
                  <p className="font-serif text-lg italic text-[#0A1128] leading-snug">“{t.quote}”</p>
                  <div className="flex items-center justify-between mt-5">
                    <div className="flex items-center gap-3">
                      {t.photo && <img src={t.photo} alt="" className="w-10 h-10 rounded-full object-cover" />}
                      <div><p className="text-sm font-medium">{t.name}</p><p className="text-xs text-[var(--slate)]">{t.role}</p></div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`edit-testimonial-${t.id}`} onClick={() => setTestDialog({ open: true, initial: t })} className="p-2 border border-[#0A1128]/20 hover:bg-[#E8E4D9]/40 transition-colors"><Pencil size={13} /></button>
                      <button onClick={() => delTestimonial(t.id)} className="p-2 text-red-600 border border-red-200 hover:bg-red-50 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {tab === "openhouses" && (
          <Section title="Open Houses" subtitle={`${openHouses.length} scheduled`}
            action={<AddBtn testid="add-open-house" onClick={() => setOhDialog({ open: true, initial: null })}>Add Open House</AddBtn>}>
            {openHouses.length === 0 ? <Empty text="No open houses scheduled." /> : (
              <div className="space-y-4">
                {openHouses.map((o) => (
                  <div key={o.id} data-testid={`admin-open-house-${o.id}`} className="bg-white border border-[#0A1128]/10 p-6 flex items-center justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-lg text-[#0A1128]">{o.property?.title || "Property to be announced"}</h3>
                      <p className="text-sm text-[var(--slate)] mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="flex items-center gap-1.5"><CalendarDays size={14} className="text-[var(--gold)]" /> {o.date}</span>
                        <span className="flex items-center gap-1.5"><Clock size={14} className="text-[var(--gold)]" /> {o.start_time}{o.end_time ? ` – ${o.end_time}` : ""}</span>
                      </p>
                      {o.note && <p className="text-xs text-[var(--slate)] mt-2">{o.note}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`edit-open-house-${o.id}`} onClick={() => setOhDialog({ open: true, initial: o })} className="flex items-center gap-1 text-sm px-4 py-2 border border-[#0A1128]/20 hover:bg-[#E8E4D9]/40 transition-colors"><Pencil size={13} /> Edit</button>
                      <button data-testid={`delete-open-house-${o.id}`} onClick={() => delOpenHouse(o.id)} className="px-3 py-2 text-red-600 border border-red-200 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {tab === "profile" && profile && (
          <>
            <ProfileEditor profile={profile} onSaved={load} />
            <ChangePassword />
          </>
        )}
      </main>

      <PropertyDialog open={propDialog.open} initial={propDialog.initial} onOpenChange={(o) => setPropDialog({ open: o, initial: o ? propDialog.initial : null })} onSave={saveProperty} />
      <TestimonialDialog open={testDialog.open} initial={testDialog.initial} onOpenChange={(o) => setTestDialog({ open: o, initial: o ? testDialog.initial : null })} onSave={saveTestimonial} />
      <OpenHouseDialog open={ohDialog.open} initial={ohDialog.initial} properties={properties} onOpenChange={(o) => setOhDialog({ open: o, initial: o ? ohDialog.initial : null })} onSave={saveOpenHouse} />
    </div>
  );
}

const Section = ({ title, subtitle, action, children }) => (
  <div>
    <div className="flex items-end justify-between mb-8">
      <div><h1 className="font-serif text-4xl text-[#0A1128]">{title}</h1><p className="text-sm text-[var(--slate)] mt-1">{subtitle}</p></div>
      {action}
    </div>
    {children}
  </div>
);
const AddBtn = ({ onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex items-center gap-2 px-5 py-3 bg-[#0A1128] text-white text-sm uppercase tracking-wider hover:bg-[#1a2342] transition-colors"><Plus size={16} /> {children}</button>
);
const Empty = ({ text }) => <div className="bg-white border border-dashed border-[#0A1128]/15 p-16 text-center text-[var(--slate)]">{text}</div>;

function ProfileEditor({ profile, onSaved }) {
  const [f, setF] = useState(profile);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setNum = (k) => (e) => setF({ ...f, [k]: parseInt(e.target.value) || 0 });
  const setList = (k) => (e) => setF({ ...f, [k]: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) });
  const setSocial = (k) => (e) => setF({ ...f, social: { ...f.social, [k]: e.target.value } });
  const uploadHeadshot = (e) => { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = () => setF({ ...f, headshot: r.result }); r.readAsDataURL(file); };
  const uploadLogo = (e) => { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = () => setF({ ...f, logo: r.result }); r.readAsDataURL(file); };

  const save = async () => {
    try { await api.put("/profile", f); toast.success("Profile updated"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-end justify-between mb-8">
        <div><h1 className="font-serif text-4xl text-[#0A1128]">Profile</h1><p className="text-sm text-[var(--slate)] mt-1">Your public information</p></div>
        <button data-testid="save-profile" onClick={save} className="px-6 py-3 bg-[#0A1128] text-white text-sm uppercase tracking-wider hover:bg-[#1a2342] transition-colors">Save Changes</button>
      </div>
      <div className="bg-white border border-[#0A1128]/10 p-8 space-y-5">
        <div className="flex items-center gap-5">
          {f.headshot && <img src={f.headshot} alt="" className="w-20 h-24 object-cover border" />}
          <label className="px-4 py-2 border border-[#0A1128]/20 text-sm cursor-pointer hover:bg-[#E8E4D9]/40 transition-colors">Upload Headshot<input type="file" accept="image/*" className="hidden" onChange={uploadHeadshot} /></label>
        </div>
        <div className="flex items-center gap-5">
          {f.logo && <img src={f.logo} alt="" className="h-16 w-auto object-contain border bg-white p-1" />}
          <label className="px-4 py-2 border border-[#0A1128]/20 text-sm cursor-pointer hover:bg-[#E8E4D9]/40 transition-colors" data-testid="upload-logo-label">Upload Logo<input data-testid="upload-logo" type="file" accept="image/*" className="hidden" onChange={uploadLogo} /></label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Name</Label><Input data-testid="profile-name" value={f.name} onChange={set("name")} /></div>
          <div><Label>Tagline</Label><Input value={f.tagline} onChange={set("tagline")} /></div>
        </div>
        <div><Label>Bio</Label><Textarea rows={4} value={f.bio} onChange={set("bio")} /></div>
        <div className="grid grid-cols-3 gap-4">
          <div><Label>Years Experience</Label><Input type="number" value={f.years_experience} onChange={setNum("years_experience")} /></div>
          <div><Label>Properties Sold</Label><Input type="number" value={f.properties_sold} onChange={setNum("properties_sold")} /></div>
          <div><Label>Total Value</Label><Input value={f.total_value} onChange={set("total_value")} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Email</Label><Input value={f.email} onChange={set("email")} /></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} /></div>
        </div>
        <div><Label>Office Address</Label><Textarea rows={2} value={f.address || ""} onChange={set("address")} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Service Areas (one per line)</Label><Textarea rows={4} value={(f.service_areas || []).join("\n")} onChange={setList("service_areas")} /></div>
          <div><Label>Credentials (one per line)</Label><Textarea rows={4} value={(f.credentials || []).join("\n")} onChange={setList("credentials")} /></div>
        </div>
        <div><Label>Awards (one per line)</Label><Textarea rows={3} value={(f.awards || []).join("\n")} onChange={setList("awards")} /></div>
        <div className="grid grid-cols-3 gap-4">
          <div><Label>Instagram</Label><Input value={f.social?.instagram || ""} onChange={setSocial("instagram")} /></div>
          <div><Label>LinkedIn</Label><Input value={f.social?.linkedin || ""} onChange={setSocial("linkedin")} /></div>
          <div><Label>Facebook</Label><Input value={f.social?.facebook || ""} onChange={setSocial("facebook")} /></div>
        </div>
      </div>
    </div>
  );
}


function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (f.new_password !== f.confirm) return toast.error("New passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password updated");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mt-12">
      <h2 className="font-serif text-3xl text-[#0A1128] mb-2">Account Security</h2>
      <p className="text-sm text-[var(--slate)] mb-6">Change your admin password.</p>
      <form onSubmit={submit} data-testid="change-password-form" className="bg-white border border-[#0A1128]/10 p-8 space-y-5">
        <div><Label>Current Password</Label><Input data-testid="cp-current" type="password" value={f.current_password} onChange={set("current_password")} required /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>New Password</Label><Input data-testid="cp-new" type="password" value={f.new_password} onChange={set("new_password")} required /></div>
          <div><Label>Confirm New Password</Label><Input data-testid="cp-confirm" type="password" value={f.confirm} onChange={set("confirm")} required /></div>
        </div>
        <button data-testid="cp-submit" disabled={loading} className="px-6 py-3 bg-[#0A1128] text-white text-sm uppercase tracking-wider hover:bg-[#1a2342] transition-colors disabled:opacity-60">
          {loading ? "Updating…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}
